import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import {
  getSecurityId,
  getSecurityCompanyName,
  getUserAssignedDepartmentList,
} from 'reducers/users';
import { getRegion } from 'reducers/region';
import BaseForm from 'containers/Base/BaseForm';
import {
  validationSchema,
  initialValues,
  initialState,
  generalSection,
  detailedSection,
} from './options';
import { getTimezone } from 'reducers/utils';
import staticText from 'static/text_display.json';
import { siteManageVisitorKey } from 'utils/constants/visitor';
import { translatedKeyValueText } from 'utils/methods/helpers';
import { SiteInfo } from 'utils/constants/informationText';
import { everyDaySchedule, startTime, endTime } from 'mocks/utils';
import { HelpLinks } from 'utils/constants/helpLinks';
import { hasService } from 'utils/helper/product';
import { serviceTypes } from 'utils/constants/serviceTypes';
import {
  checkIfSitePatrolIsEnabled,
  getSecurityCompanyServices,
} from 'reducers/securityCompany';

import {
  getGlobalDepartmentRecords,
  getSiteListOptions,
} from 'selectors/globalSelectors';
import isEmpty from 'lodash/isEmpty';
import { getItemOrDefaultToValue } from 'utils/details';
import {
  siteSelectors,
  siteDataGridActions,
} from 'containers/Pages/Locations/Sites';
import { actions as embeddedFormActions } from 'modules/embeddedForms/reducer';

const BaseSiteForm = ({
  editMode,
  closeForm,
  // for embedded version of this form
  disablePresist,
  embeddedForm,
  persistentStateName,
}) => {
  //-------------------useState hook------------------------------------
  const [selectedBusinessClient, setSelectedBusinessClient] = useState();
  const [toggleManageVisitor, setManageVisitor] = useState();
  const [scheduledRequired, setScheduleRequired] = useState(false);
  const [allDay, setAllDay] = useState(false);
  const [everyDay, setEveryDay] = useState(false);
  const [selectedSiteLocation, setSiteLocation] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  //-------------------Selectors------------------------------------------
  const details = useSelector(state =>
    siteSelectors.getSiteDetailsTransformed(state),
  );
  const userAssignedDepartments = useSelector(state =>
    getUserAssignedDepartmentList(state),
  );
  const businessClientEmbeddedDetails = useSelector(
    state => siteSelectors.businessClientEmbeddedDetails(state),
    shallowEqual,
  );
  const customerEmbeddedDetails = useSelector(
    state => siteSelectors.customerEmbeddedDetails(state),
    shallowEqual,
  );
  const securityCompanyId = useSelector(state => getSecurityId(state));
  const securityCompanyName = useSelector(state =>
    getSecurityCompanyName(state),
  );
  const region = useSelector(state => getRegion(state));
  const countries = useSelector(state => state.utils.countries);
  const languages = useSelector(state => state.utils.languages);
  const siteList = useSelector(state => getSiteListOptions(state));
  const services = useSelector(state => getSecurityCompanyServices(state));
  const timezone = useSelector(state => getTimezone(state));
  const globalDepartmentRecords = useSelector(state =>
    getGlobalDepartmentRecords(state),
  );
  const IsSitePatrolEnabled = useSelector(state =>
    checkIfSitePatrolIsEnabled(state),
  );
  //--------------------Actions-------------------------------------------
  const dispatch = useDispatch();
  const dispatchCreateSite = useCallback(
    (payload, patrolData, embeddedForm) =>
      dispatch(
        siteDataGridActions.createRowEntry({
          site: payload,
          patrolData,
          embeddedForm,
        }),
      ),
    [dispatch],
  );
  const dispatchUpdateSite = useCallback(
    ({ id, payload }) =>
      dispatch(siteDataGridActions.updateRowEntry({ id, site: payload })),
    [dispatch],
  );

  const visitor = hasService({
    services,
    service: serviceTypes.visitorManagement,
  });

  const handlePatrolSelectEveryday = patrol => {
    if (patrol.everyDay) {
      patrol.schedule = everyDaySchedule;
    } else if (everyDay && !patrol.everyDay) {
      patrol.schedule = undefined;
    }
    setEveryDay(patrol.everyDay);
  };
  const handlePatrolSetAllDay = patrol => {
    if (patrol.allDay) {
      setAllDay(true);
      patrol.startTime = startTime;
      patrol.endTime = endTime;
    } else if (allDay && !patrol.allDay) {
      setAllDay(false);
      patrol.startTime = undefined;
      patrol.endTime = undefined;
    }
  };

  const onChange = value => {
    setSelectedDepartments(value?.departments);

    if (!isEmpty(value.businessClient)) {
      setSelectedBusinessClient(value.businessClient);
    } else if (value.businessClient === null || value.businessClient === '') {
      value.businessClient = '';
      value.contact = '';
      setSelectedBusinessClient(null);
    }

    if (value.newBusinessClient) {
      value.newSiteContact = false;
    } else {
      value.newContact = false;
    }
    setManageVisitor(!value[siteManageVisitorKey]);

    if (value.site) {
      const site = siteList.find(site => site.id === value.site);
      setSiteLocation(get(site, 'locationDetail', []));
    }
    if (value.schedule) {
      setScheduleRequired(true);
    } else {
      setScheduleRequired(false);
    }
    handlePatrolSelectEveryday(value);
    handlePatrolSetAllDay(value);
  };

  const onCreate = async values => {
    const patrolData = null;
    const ids = values?.departments &&
      values?.departments.length > 0 && {
        departmentIds: getItemOrDefaultToValue(
          values,
          'departments',
          undefined,
        )?.map(m => m.id),
      };
    const purposes = values.purposes.map(p => {
      const { purpose } = p;
      return purpose.value;
    });
    const payload = {
      ...values,
      purposes,
      securityCompanyName,
      securityCompanyId,
      contact: values?.contact?.value,
      regionId: region.id,
      regionName: region.name,
      ...(ids && { departmentIds: ids.departmentIds }),
    };
    dispatchCreateSite(payload, patrolData, embeddedForm);
    if (embeddedForm) {
      closeForm();
    }
    // close this form in case it is opened as an embedded form
  };

  const onEdit = values => {
    const ids = values?.departments &&
      values?.departments.length > 0 && {
        departmentIds: getItemOrDefaultToValue(
          values,
          'departments',
          undefined,
        )?.map(m => m.id),
      };
    const purposes = values.purposes.map(p => {
      const { purpose } = p;
      return purpose.value;
    });
    const id = get(details, 'others.id.value');

    const payload = {
      ...values,
      purposes,
      securityCompanyName,
      securityCompanyId,
      contact: values?.contact?.value,
      regionId: region.id,
      regionName: region.name,
      ...(ids && { departmentIds: ids.departmentIds }),
    };
    dispatchUpdateSite({ id, payload });
  };

  const filteredDepartments =
    !isEmpty(selectedBusinessClient) &&
    !isEmpty(selectedBusinessClient.departmentIds)
      ? userAssignedDepartments.filter(f =>
          selectedBusinessClient.departmentIds.includes(f.id),
        )
      : userAssignedDepartments;

  const transformedInitialValues = editMode
    ? initialValues(details)
    : initialState(timezone);

  const generalSectionOptions = generalSection({
    selectedBusinessClient,
    countries,
    languages,
    everyDay,
    scheduledRequired,
    allDay,
    selectedSiteLocation,
    editMode,
    userAssignedDepartments: filteredDepartments,
    IsSitePatrolEnabled,
    securityCompanyId,
    globalDepartmentRecords,
    selectedDepartments,
    updatedValues: {
      // only pass embedded values in this key
      businessClient: businessClientEmbeddedDetails,
      contact: customerEmbeddedDetails,
    },
  });

  useEffect(() => {
    return () => {
      // clear embedded details
      dispatch(embeddedFormActions.clearEmbeddedFormDetails());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BaseForm
      onChange={onChange}
      imageRequired={false}
      multipleImageRequired={true}
      isImageMandatory={false}
      imageType="Establishment"
      edit={editMode}
      initialValues={transformedInitialValues}
      formTitle={
        editMode
          ? staticText.managerial_view.site.edit_default
          : staticText.managerial_view.site.create
      }
      closeForm={closeForm}
      saveHoverText={
        editMode
          ? translatedKeyValueText('update_value_title', {
              value: details?.generalInfo?.name.value,
            })
          : staticText.managerial_view.site.create
      }
      onSubmit={editMode ? onEdit : onCreate}
      validationSchema={validationSchema({
        scheduledRequired,
      })}
      generalSection={generalSectionOptions}
      detailedSection={detailedSection({
        toggleManageVisitor,
        visitor,
      })}
      hoverText={editMode ? details?.generalInfo?.name.value : ''}
      note={SiteInfo.note}
      formInfo={{
        link: HelpLinks.siteLink,
        value: HelpLinks.siteText,
      }}
      disablePresist={disablePresist}
      persistentStateName={persistentStateName}
    />
  );
};

BaseSiteForm.propTypes = {
  editMode: PropTypes.bool,
  disablePresist: PropTypes.bool,
  embeddedForm: PropTypes.bool,
  closeForm: PropTypes.func,
  persistentStateName: PropTypes.string,
};

export default BaseSiteForm;
